import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { ConciergeAdminAvailability } from 'restaurantAdmin/reservations/concierge/ConciergeAdminAvailability';
import buttonStyles from '@components/button/Button.scss';
import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { TZDate } from '@date-fns/tz';
import { DateFilterPill } from '@shared/availability/DateFilterPill';
import { DateFilterPopover } from '@shared/availability/DateFilterPopover';
import { GuestCountFilterPill } from '@shared/availability/GuestCountFilterPill';
import { GuestCountFilterPopover } from '@shared/availability/GuestCountFilterPopover';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import { useEffect } from 'react';
import typography from '~styles/typography.scss';
import { useConciergeAvailabilityContext } from '../concierge/ConciergeAvailabilityContext';
import { InteractiveConciergeFloorPlan } from '../concierge/InteractiveConciergeFloorPlan';
import { ServiceReservation, updateReservation } from './apiHelpers';
import styles from './EditSingleTableReservationDrawer.scss';
import { useServiceSearchParams } from './hooks/useServiceSearchParams';

const useAdminUpdateReservation = ({
  reservationId,
}: {
  reservationId: string;
}) => {
  const restaurant = useRestaurant();
  const queryClient = useQueryClient();

  const { selectedTime, selectedGuestCount, selectedAvailability } =
    useConciergeAvailabilityContext();
  const selectedListingId = selectedAvailability?.listing.id;

  const handleUpdateReservation = async () => {
    await updateReservation({
      restaurantId: restaurant.id,
      reservationId: reservationId,
      date: selectedAvailability!.date!,
      guestCount: selectedGuestCount,
      time: selectedTime!,
      listingId: selectedListingId!,
      wasTableChosen: selectedAvailability!.isBestAvailable,
    });
  };

  const updateReservationMutation = useMutation({
    mutationFn: handleUpdateReservation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'restaurants',
          restaurant.id,
          'occupants',
          'reservation',
          reservationId,
        ],
      });
      successToast({
        message: 'Reservation successfully updated!',
      });
    },
    onError: () => {
      errorToast({
        message: 'There was an error updating the reservation.',
      });
    },
  });

  return { handleUpdateReservation: updateReservationMutation };
};

interface EditSingleTableReservationDrawerProps {
  reservation: ServiceReservation;
}

export const EditSingleTableReservationDrawer = ({
  reservation,
}: EditSingleTableReservationDrawerProps) => {
  const { maxReservationGuests, timezone } = useRestaurant();
  const { exitReservationEditMode } = useServiceSearchParams();
  const {
    selectedDate,
    selectedGuestCount,
    setSelectedDate,
    setSelectedGuestCount,
    clearSelectedAvailability,
    floorPlans,
    selectedFloorPlan,
    setSelectedFloorPlanId,
    isLoading,
  } = useConciergeAvailabilityContext();

  const { handleUpdateReservation } = useAdminUpdateReservation({
    reservationId: reservation.id,
  });

  useEffect(() => {
    setSelectedGuestCount(reservation.guestCount);
  }, [reservation.id]);

  const {
    close: closeGuestCountFilter,
    isOpen: isGuestCountFilterOpen,
    open: openGuestCountFilter,
  } = useIsOpen();

  const {
    close: closeDateFilter,
    isOpen: isDateFilterOpen,
    open: openDateFilter,
  } = useIsOpen();

  const handleGuestCountSelection = (appliedGuestCount: number) => {
    setSelectedGuestCount(appliedGuestCount);
    closeGuestCountFilter();
  };

  const handleDateSelection = (appliedDate: Date) => {
    setSelectedDate(appliedDate);
    closeDateFilter();
  };

  const handleEditReservation = async () => {
    await handleUpdateReservation.mutateAsync();
    clearSelectedAvailability();
    exitReservationEditMode();
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.filterPills}>
        <GuestCountFilterPill
          closeFilter={closeGuestCountFilter}
          isOpenFilter={isGuestCountFilterOpen}
          openFilter={openGuestCountFilter}
          selectedGuestCount={selectedGuestCount}
          popover={
            <GuestCountFilterPopover
              onGuestCountSelection={handleGuestCountSelection}
              maxReservationGuests={maxReservationGuests}
            />
          }
        />

        <DateFilterPill
          openFilter={openDateFilter}
          closeFilter={closeDateFilter}
          isOpenFilter={isDateFilterOpen}
          selectedDate={selectedDate}
          popover={
            <DateFilterPopover
              onDateSelection={handleDateSelection}
              selectedDate={selectedDate}
              minimumDate={TZDate.tz(timezone)}
            />
          }
        />
        {floorPlans.length > 0 && (
          <FloorPlanSelector
            fullWidth
            floorPlans={floorPlans}
            selectedFloorPlan={selectedFloorPlan}
            onChange={(selectedFloorPlanId) => {
              setSelectedFloorPlanId(selectedFloorPlanId);
            }}
          />
        )}
      </div>

      {selectedFloorPlan.id && (
        <InteractiveConciergeFloorPlan
          backgroundSrc={selectedFloorPlan.backgroundSrc}
          tables={selectedFloorPlan.floorPlanTables}
          darkBackground
        />
      )}

      <ConciergeAdminAvailability
        availabilityItemAction={
          <button
            type="button"
            className={cx(
              typography.h8,
              buttonStyles.btn,
              buttonStyles.btnPrimary,
            )}
            onClick={handleEditReservation}
          >
            Assign this table
          </button>
        }
      />
    </div>
  );
};
